import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';

interface RemoveTourDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeName: string;
  onUndoPick: () => void | Promise<void>;
  onKeepPick: () => void | Promise<void>;
}

export const RemoveTourDialog: React.FC<RemoveTourDialogProps> = ({
  open,
  onOpenChange,
  storeName,
  onUndoPick,
  onKeepPick,
}) => {
  const [loading, setLoading] = React.useState<'undo' | 'keep' | null>(null);

  const run = async (kind: 'undo' | 'keep', fn: () => void | Promise<void>) => {
    setLoading(kind);
    try {
      await fn();
      onOpenChange(false);
    } catch (error) {
      console.error('Remove from tour failed:', error);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (loading) return;
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-50">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <AlertTriangle size={24} className="text-amber-700" />
              </div>
              <Dialog.Title className="text-xl font-black text-gray-900">
                Remove from tour?
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-gray-600 text-sm mb-4">
            {storeName} leaves this tour. Other stops stay. An open Google Maps
            route will not update — tap Next or Remaining again.
          </Dialog.Description>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => void run('undo', onUndoPick)}
              className="min-h-[48px] px-4 bg-red-600 text-white rounded-lg font-bold disabled:opacity-50"
            >
              {loading === 'undo' ? 'Removing...' : 'Undo pick (delete draft note)'}
            </button>
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => void run('keep', onKeepPick)}
              className="min-h-[48px] px-4 bg-brand-600 text-white rounded-lg font-bold disabled:opacity-50"
            >
              {loading === 'keep' ? 'Removing...' : 'Keep pick for later'}
            </button>
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => onOpenChange(false)}
              className="min-h-[48px] px-4 bg-gray-200 text-gray-700 rounded-lg font-bold"
            >
              Cancel
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
